// Some LLMs wrap their entire markdown output in a triple-backtick code
// fence (often ```markdown … ``` ) when asked to produce a document,
// ignoring the prompt's "no surrounding code fences" instruction. The
// parser would otherwise see the leading ``` as line one, never find
// frontmatter or H2 headings, and fail with a generic "no recognized
// sections" message that points users at the wrong problem.
//
// Strip the fence ONLY when it wraps the entire document end-to-end:
// the first non-empty line is a triple-backtick (with optional `markdown`
// / `md` language tag), the last non-empty line is a triple-backtick,
// and there are no column-0 triple-backtick lines in between.
//
// Inline fences inside YAML quoted strings live nested under a property
// bullet (indented at least two spaces), so they don't appear at
// column 0 and don't trigger the disqualification.

const OPENING_FENCE = /^```(?:markdown|md)?\s*$/i;
const CLOSING_FENCE = /^```\s*$/;
const COLUMN_ZERO_FENCE = /^```/;

export function stripDocumentFence(markdown: string): string {
  const lines = markdown.split("\n");

  let firstIdx = -1;
  let lastIdx = -1;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (line !== undefined && line.trim().length > 0) {
      firstIdx = i;
      break;
    }
  }
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    if (line !== undefined && line.trim().length > 0) {
      lastIdx = i;
      break;
    }
  }
  if (firstIdx === -1 || lastIdx === -1 || firstIdx === lastIdx) return markdown;

  const first = lines[firstIdx];
  const last = lines[lastIdx];
  if (first === undefined || last === undefined) return markdown;
  if (!OPENING_FENCE.test(first) || !CLOSING_FENCE.test(last)) return markdown;

  for (let i = firstIdx + 1; i < lastIdx; i += 1) {
    const line = lines[i];
    if (line !== undefined && COLUMN_ZERO_FENCE.test(line)) return markdown;
  }

  return lines.slice(firstIdx + 1, lastIdx).join("\n");
}
